/**
 * agb-display-state.js — "is this accessory actually ON DISPLAY?"
 * Dorian 2026-07-20.
 *
 * THE PROBLEM
 * Accessory Y depends on accessory X. The dependency gate asked only
 * `Store.hasAccepted(X)` — pure membership of the accepted list. But two
 * accessories that share an `emplacement` (the same physical spot on the
 * carrier) can BOTH be accepted: that is deliberate, you're allowed to buy two
 * mag pouches. The canvas then draws only the LAST-added one and hides the
 * other. So X could be accepted-but-hidden, the gate still said "satisfied",
 * and Y got composited onto the carrier with its mount nowhere in sight —
 * exactly the case the gate exists to prevent.
 *
 * THE PREDICATE
 *   satisfied(d)  ==  accepted(d) AND NOT suppressed(d)
 *   suppressed(d) ==  d has an emplacement AND a later-accepted sibling won it
 * evaluated TRANSITIVELY: if d is only displayable because of its own parent,
 * and that parent is suppressed, then d is not really on display either.
 *
 * WHY A SEPARATE FILE
 * Both consumers (agb-canvas.js `_unmetDepNames`, agb-suggest.js
 * `hasUnmetDependencies`) ship inside agb-uipack.min.js, and the natural shared
 * home (agb-store.js) is inside agb-corepack.min.js, which has no builder. A
 * standalone asset ships raw, needs no build, and keeps one source of truth
 * instead of the same predicate drifting across two files and three stores.
 *
 * FAIL-SAFE: every consumer calls this behind `window.AGB_DISPLAY ? … : old`,
 * so if this file is absent the behaviour is exactly what it is today.
 */
(function () {
  'use strict';

  function storeState() {
    try {
      var AGB = window.AGB;
      if (AGB && typeof AGB.require === 'function') {
        var st = AGB.require('store');
        if (st && typeof st.getState === 'function') return st.getState();
      }
    } catch (e) { /* builder not booted on this page */ }
    return null;
  }

  function acceptedList() {
    var st = storeState();
    if (st && Array.isArray(st.acceptedItems)) return st.acceptedItems;
    return Array.isArray(window.acceptedItems) ? window.acceptedItems : [];
  }

  /* Panel-managed accessory record for the CURRENT product/base — mirrors
     getDynAccessory() in agb-canvas.js so both agree on which entry wins. */
  function dynAccessory(handle) {
    try {
      var dyn = window.__AGB_GEAR_SUGG;
      if (!dyn || typeof dyn.accessoryFor !== 'function') return null;
      var st = storeState();
      return dyn.accessoryFor(
        handle,
        (st && st.product && st.product.handle) || null,
        (st && st.baseItem && st.baseItem.handle) || null
      ) || null;
    } catch (e) { return null; }
  }

  function emplacementOf(handle) {
    var a = dynAccessory(handle);
    return (a && typeof a.emplacement === 'string') ? a.emplacement.trim() : '';
  }

  function isAccepted(handle) {
    if (!handle) return false;
    var list = acceptedList();
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].handle === handle) return true;
    }
    return false;
  }

  /* Last ACCEPTED item wins each shared spot — same rule, same iteration order
     as the canvas paint loop, so the two can never disagree. */
  function winners() {
    var w = {};
    var list = acceptedList();
    for (var i = 0; i < list.length; i++) {
      var h = list[i] && list[i].handle;
      if (!h) continue;
      var e = emplacementOf(h);
      if (e) w[e] = h;          // later iteration overwrites = last-added wins
    }
    return w;
  }

  function isSuppressed(handle, _w) {
    if (!handle) return false;
    var e = emplacementOf(handle);
    if (!e) return false;                       // no shared spot => never hidden
    var w = _w || winners();
    return !!(w[e] && w[e] !== handle);
  }

  function dependenciesOf(handle) {
    var out = [];
    try {
      var AGB = window.AGB;
      var Store = (AGB && typeof AGB.require === 'function') ? AGB.require('store') : null;
      var map = Store && Store.SUGGESTION_DEPENDENCIES;
      var d = map && map[handle];
      if (typeof d === 'string') out.push(d);
      else if (Array.isArray(d)) d.forEach(function (x) { if (typeof x === 'string') out.push(x); });
    } catch (e) { /* corepack shape differs or not booted */ }
    var a = dynAccessory(handle);
    var req = (a && typeof a.requires === 'string') ? a.requires.trim() : '';
    if (req && out.indexOf(req) === -1) out.push(req);
    return out;
  }

  /* accepted AND on display — transitively. `seen` breaks any dependency cycle
     a panel misconfiguration could introduce. */
  function isDepSatisfied(dep, seen, _w) {
    if (!dep) return true;
    seen = seen || {};
    if (seen[dep]) return true;                 // cycle: don't recurse forever
    seen[dep] = true;
    if (!isAccepted(dep)) return false;
    var w = _w || winners();
    if (isSuppressed(dep, w)) return false;
    var parents = dependenciesOf(dep);
    for (var i = 0; i < parents.length; i++) {
      if (!isDepSatisfied(parents[i], seen, w)) return false;
    }
    return true;
  }

  window.AGB_DISPLAY = {
    isAccepted: isAccepted,
    isSuppressed: function (h) { return isSuppressed(h); },
    isDepSatisfied: function (d) { return isDepSatisfied(d); },
    emplacementOf: emplacementOf,
    winners: winners
  };
})();
